import { Box, Container, Heading, Text, Flex, Stack, Spacer, Button, useDisclosure, SimpleGrid, Image } from "@chakra-ui/react";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "scenes/layouts";
import LoadingScreen from "components/loading-screen";
import StoreOrderService from "services/store-order/store-order-service";
import moment from "moment";
import OrderItem from "./order-item";
import { OrderSummary } from "./order-summary";
import OrderPayManualDialog from "./pay-manual";
import StoreOrderUpdateAirWayDialog from "./update-airway";
import ConfirmDialog from "../../../components/confirm-dialog";

const OrderDetailScene = (): ReactElement => {
    const [isLoading, setLoading] = useState<boolean>(true);
    const [order, setOrder] = useState<any>(null);
    const { orderId }: any = useParams();
    const { isOpen: isShowPayManualDialog, onOpen: onShowPayManualDialog, onClose: onClosePayManualDialog } = useDisclosure();
    const { isOpen: isShowUpdateAirWayDialog, onOpen: onShowUpdateAirWayDialog, onClose: onCloseUpdateAirWayDialog } = useDisclosure();
    const { isOpen: isShowCompleteDialog, onOpen: onShowCompleteDialog, onClose: onCloseCompleteDialog } = useDisclosure();
    const [isSubmittingOrderComplete, setIsSubmittingOrderComplete] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        StoreOrderService.Get(orderId).then((response: any) => {
            setOrder(response.result);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const handleRefreshData = (order: any) => {
        setOrder(order);
        onClosePayManualDialog();
        onCloseUpdateAirWayDialog();
    }

    const handleCompleteOrder = () => {
        setIsSubmittingOrderComplete(true);
        StoreOrderService.MarkAsComplete(orderId).then((res: any) => {
            handleRefreshData(res.result);
            onCloseCompleteDialog();
        }).finally(() => {
            setIsSubmittingOrderComplete(false);
        });
    };

    if (isLoading)
        return <LoadingScreen />;

    return (
        <Layout>
            <Container maxWidth={"8xl"} mt={5} mb={5}>
                <Box rounded={'xl'} mt={3} p={0} pt={4} border={'1px solid '} borderColor={'gray.200'} bgColor={'white'}>
                    <Box
                        maxW={{ base: '3xl', lg: '7xl' }}
                        mx="auto"
                        px={{ base: '4', md: '8', lg: '2' }}
                        py={{ base: '6', md: '8', lg: '6' }}
                    >
                        <Stack direction={{ base: 'column', lg: 'row' }} align={{ lg: 'flex-start' }} spacing={{ base: '8', md: '8' }}>
                            <Stack spacing={{ base: '8', md: '10' }} flex="2">
                                <Heading fontSize="xl" fontWeight="extrabold">
                                    Pesanan {order.orderNumber} - {order.statusString}
                                </Heading>

                                <Box>
                                    <Flex mb={2}>
                                        <Text fontSize={'md'} fontWeight={'medium'}>Alamat Pengiriman</Text>
                                        <Spacer />
                                        <Box>
                                            <Text fontSize={'sm'} fontWeight={'medium'}>{order.shipment.expeditionService.fullName}</Text>
                                            <Text fontSize={'xs'}>{order.shipment.airWayBill || "-"}</Text>
                                        </Box>
                                    </Flex>
                                    <Flex gap={4} direction={{ base: "column", md: "row" }}>
                                        <Box>
                                            <Text fontSize={'sm'}>{order.shipment.customerName}</Text>
                                            <Text fontSize={'xs'}>{order.shipment.phoneNumber}</Text>
                                            <Text fontSize={'xs'}>{order.shipment.address}</Text>
                                            <Text fontSize={'xs'}>{order.shipment.district.name}, {order.shipment.district.city.name}, {order.shipment.district.city.province.name}, {order.shipment.district.city.postalCode}</Text>
                                        </Box>
                                        <Box borderLeft={'1px solid'} borderColor={'gray.300'} pl={4}>
                                            {
                                                (order.status === 40 || order.status === 50) &&
                                                <Box>
                                                    {
                                                        order.shipment.proofOfDeliveryReceiver &&
                                                        <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align="center" columnGap={4} fontSize={'sm'} alignItems={'baseline'} mb={2}>
                                                            <Text fontWeight={'medium'}>{moment(order.shipment.proofOfDeliveryDate).format("DD-MM-yyyy HH:mm")}</Text>
                                                            <Text flex={1}>Paket telah diterima oleh {order.shipment.proofOfDeliveryReceiver}</Text>
                                                        </Flex>
                                                    }
                                                    {
                                                        order.shipment.trackings.map((tracking: any) => {
                                                            return (
                                                                <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align="center" columnGap={4} fontSize={'sm'} alignItems={'baseline'} mb={2} key={tracking.trackingDate}>
                                                                    <Text fontWeight={'medium'}>{moment(tracking.trackingDate).format("DD-MM-yyyy HH:mm")}</Text>
                                                                    <Text flex={1}>{tracking.description} {tracking.city}</Text>
                                                                </Flex>
                                                            )
                                                        })
                                                    }
                                                </Box>
                                            }
                                        </Box>
                                    </Flex>
                                </Box>

                                <Stack spacing="6">
                                    {
                                        order.items.map((item: any) => (
                                            <OrderItem key={item.product.id} image={item.product.images[0].imageUrl} name={item.product.name} amount={item.total} quantity={item.quantity} />
                                        ))
                                    }
                                </Stack>

                                <Box>
                                    <Heading fontSize={'sm'} fontWeight={'medium'}>Pesan</Heading>
                                    <Text fontSize={'sm'} as={'i'}>{order.note || '-'}</Text>
                                </Box>
                            </Stack>

                            <Flex direction="column" align="center" flex="1">
                                <OrderSummary order={order} />
                                {
                                    order.status === 10 && <Button mt={3} isFullWidth={true} onClick={onShowPayManualDialog}>Bayar Manual</Button>
                                }
                                {
                                    isShowPayManualDialog &&
                                    <OrderPayManualDialog
                                        order={order}
                                        onClose={onClosePayManualDialog}
                                        onRefreshData={handleRefreshData}
                                    />
                                }


                                {
                                    order.status === 30 && <Button mt={3} isFullWidth={true} onClick={onShowUpdateAirWayDialog}>Update Nomor Resi</Button>
                                }
                                {
                                    isShowUpdateAirWayDialog &&
                                    <StoreOrderUpdateAirWayDialog
                                        order={order}
                                        onClose={onCloseUpdateAirWayDialog}
                                        onRefreshData={handleRefreshData}
                                    />
                                }

                                {
                                    (order.status === 30 || order.status === 40) && <Button mt={3} isFullWidth={true} onClick={onShowCompleteDialog}>Selesaikan</Button>
                                }
                                {
                                    isShowCompleteDialog &&
                                    <ConfirmDialog
                                        title={'Selesaikan Pesanan'}
                                        message={`Dengan klik tombol Selesaikan, maka Pesanan ini Akan diselesaikan dan tidak bisa diubah kembali. Anda yakin untuk Selesaikan Pesanan ${order.orderNumber}?`}
                                        colorScheme={'red'}
                                        buttonLabel={'Selesaikan'}
                                        onSubmit={handleCompleteOrder}
                                        onClose={onCloseCompleteDialog}
                                        isLoading={isSubmittingOrderComplete}
                                    />
                                }
                            </Flex>
                        </Stack>
                    </Box>
                </Box>

                {
                    order.paymentMethod === 10 && order.status !== 0 && order.status !== 10 && order.status !== 60 &&
                    <Box rounded={'xl'} mt={3} p={0} pt={4} border={'1px solid '} borderColor={'gray.200'} bgColor={'white'}>
                        <Box
                            maxW={{ base: '3xl', lg: '7xl' }}
                            mx="auto"
                            px={{ base: '4', md: '8', lg: '2' }}
                            py={{ base: '6', md: '8', lg: '6' }}
                        >
                            <Text fontSize={'md'} fontWeight={'medium'}>Pembayaran</Text>

                            {
                                order.paymentHistories.map((paymentManual: any) => {
                                    return (
                                        <Box mt={4} fontSize={'sm'} key={paymentManual.id}>
                                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 2, lg: 4 }}>
                                                <Text>Tanggal Pembayaran: <strong>{moment(paymentManual.paymentDate).format("DD MMMM yyyy HH:mm")}</strong></Text>
                                                <Text>Bayar Dari: <strong>{paymentManual.bankName}</strong></Text>
                                                <Text>Nomor Rekening: <strong>{paymentManual.bankAccountNumber}</strong></Text>
                                                <Text>Nama Rekening: <strong>{paymentManual.bankAccountName}</strong></Text>
                                                <Text>Bayar Ke: <strong>{`${paymentManual.logosBankAccount.bankName} - ${paymentManual.logosBankAccount.bankAccountNumber}`}</strong></Text>
                                                <Text>Total yang dibayar: <strong>Rp {String.toFormatSeparator(paymentManual.amount)}</strong></Text>
                                            </SimpleGrid>
                                            {
                                                paymentManual.bankAccountNumber &&
                                                <Image height={'200px'} mt={2} src={paymentManual.receiptUrl} />
                                            }
                                        </Box>);
                                })
                            }
                        </Box>
                    </Box>
                }
            </Container>
        </Layout>
    );
};

export default OrderDetailScene;